import { useState, useCallback, useEffect, useRef } from 'react'
import { useAuth } from '../auth/AuthContext'
import { signOut } from '../firebase'
import { useYear } from '../auth/YearContext'
import { COMPANIES } from '../data/companies'
import { SkattekontoCard } from '../components/SkattekontoCard'
import { MomsBalanceCard } from '../components/MomsBalanceCard'
import { ProfitLossCard } from '../components/ProfitLossCard'
import { ReceiptsCard } from '../components/ReceiptsCard'
import { VillkoratTillskottCard } from '../components/VillkoratTillskottCard'
import { ExportButton } from '../components/ExportButton'
import { Import } from './Import'
import { Receipts } from './Receipts'
import { Logs } from './Logs'
import { Overview } from './Overview'
import {
  subscribeDashboardPrefs,
  saveDashboardPrefs,
  type DashboardPrefs,
} from '../lib/cloudStore'
import { startDashboardSync } from '../lib/dashboardSync'

type View = 'dashboard' | 'overview' | 'import' | 'receipts' | 'logs'
type CardKey = 'skatte' | 'pl' | 'moms' | 'kvitton' | 'tillskott'
type CardSize = 'min' | 'med' | 'max'

const DEFAULT_ORDER: CardKey[] = ['skatte', 'pl', 'moms', 'kvitton', 'tillskott']
const ALL_CARD_KEYS: CardKey[] = DEFAULT_ORDER
const VALID_VIEWS: View[] = ['dashboard', 'overview', 'import', 'receipts', 'logs']
const SIZE_CYCLE: CardSize[] = ['min', 'med', 'max']

function getViewFromHash(): View {
  const h = window.location.hash.replace('#', '')
  return VALID_VIEWS.includes(h as View) ? (h as View) : 'dashboard'
}

/** Render a card in the right size mode */
function renderCard(
  key: CardKey,
  companyId: string,
  companyName: string,
  size: CardSize,
  onSize: (s: CardSize) => void,
) {
  // collapsed = minimized pill
  if (size === 'min') {
    return renderCardInner(key, companyId, companyName, true, false, onSize)
  }
  // medium = compact card
  if (size === 'med') {
    return renderCardInner(key, companyId, companyName, false, true, onSize)
  }
  // max = full card
  return renderCardInner(key, companyId, companyName, false, false, onSize)
}

function renderCardInner(
  key: CardKey,
  companyId: string,
  companyName: string,
  collapsed: boolean,
  medium: boolean,
  onSize: (s: CardSize) => void,
) {
  const props = {
    companyId,
    companyName,
    collapsed,
    medium,
    onMin: () => onSize('min'),
    onMed: () => onSize('med'),
    onMax: () => onSize('max'),
  }
  switch (key) {
    case 'skatte': return <SkattekontoCard {...props} />
    case 'pl': return <ProfitLossCard {...props} />
    case 'moms': return <MomsBalanceCard {...props} />
    case 'kvitton': return <ReceiptsCard {...props} />
    case 'tillskott': return <VillkoratTillskottCard {...props} />
  }
}

export function Dashboard() {
  const { user } = useAuth()
  const { year, setYear, availableYears } = useYear()
  const [view, _setView] = useState<View>(getViewFromHash)

  const setView = useCallback((v: View) => {
    _setView(v)
    window.location.hash = v === 'dashboard' ? '' : v
  }, [])

  useEffect(() => {
    const onHash = () => _setView(getViewFromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  // --- Firebase-persisted prefs ---
  const [cardOrder, setCardOrder] = useState<Record<string, CardKey[]>>({})
  const [cardSizes, setCardSizes] = useState<Record<string, Record<CardKey, CardSize>>>({})
  const prefsLoaded = useRef(false)
  const saveTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    try {
      return subscribeDashboardPrefs((prefs) => {
        if (prefs.cardOrder) setCardOrder(prefs.cardOrder as Record<string, CardKey[]>)
        if (prefs.collapsed) {
          // Migrate old boolean collapsed -> new CardSize
          const sizes: Record<string, Record<CardKey, CardSize>> = {}
          for (const [cid, states] of Object.entries(prefs.collapsed)) {
            sizes[cid] = {} as Record<CardKey, CardSize>
            for (const [k, v] of Object.entries(states)) {
              if (typeof v === 'string' && SIZE_CYCLE.includes(v as CardSize)) {
                sizes[cid][k as CardKey] = v as CardSize
              } else {
                sizes[cid][k as CardKey] = v ? 'min' : 'max'
              }
            }
          }
          setCardSizes(sizes)
        }
        prefsLoaded.current = true
      })
    } catch {
      prefsLoaded.current = true
    }
  }, [])

  // --- Dashboard sync (POST aggregated data to dashboard-api-worker) ---
  useEffect(() => {
    return startDashboardSync(year)
  }, [year])

  const savePrefs = useCallback((order: Record<string, CardKey[]>, sizes: Record<string, Record<CardKey, CardSize>>) => {
    if (!prefsLoaded.current) return
    if (saveTimeout.current) clearTimeout(saveTimeout.current)
    saveTimeout.current = setTimeout(() => {
      saveDashboardPrefs({ cardOrder: order, collapsed: sizes as any }).catch((e) => console.warn('[Dashboard] savePrefs failed (silent ok for UX):', e))
    }, 600)
  }, [])

  const getOrder = (companyId: string): CardKey[] => {
    const saved = cardOrder[companyId]
    if (!saved || saved.length === 0) return DEFAULT_ORDER
    // Behåll sparad ordning; lägg till nya kort (t.ex. tillskott) sist
    const known = saved.filter((k): k is CardKey => ALL_CARD_KEYS.includes(k as CardKey))
    const missing = ALL_CARD_KEYS.filter((k) => !known.includes(k))
    return known.length > 0 ? [...known, ...missing] : DEFAULT_ORDER
  }

  const getSize = (companyId: string, card: CardKey): CardSize =>
    cardSizes[companyId]?.[card] ?? 'max'

  const setSize = useCallback((companyId: string, card: CardKey, size: CardSize) => {
    setCardSizes((prev) => {
      const next = { ...prev, [companyId]: { ...prev[companyId], [card]: size } }
      setCardOrder((o) => { savePrefs(o, next); return o })
      return next
    })
  }, [savePrefs])

  // --- Drag and drop ---
  const [dragCompany, setDragCompany] = useState<string | null>(null)
  const [dragKey, setDragKey] = useState<CardKey | null>(null)
  const [dropTarget, setDropTarget] = useState<CardKey | null>(null)

  const onDragStart = (companyId: string, key: CardKey) => (e: React.DragEvent) => {
    setDragCompany(companyId)
    setDragKey(key)
    e.dataTransfer.effectAllowed = 'move'
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5'
    }
  }

  const onDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1'
    }
    setDragCompany(null)
    setDragKey(null)
    setDropTarget(null)
  }

  const onDragOver = (companyId: string, key: CardKey) => (e: React.DragEvent) => {
    if (dragCompany !== companyId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDropTarget(key)
  }

  const onDrop = (companyId: string, key: CardKey) => (e: React.DragEvent) => {
    e.preventDefault()
    if (dragCompany !== companyId || !dragKey || dragKey === key) {
      setDropTarget(null)
      return
    }
    setCardOrder((prev) => {
      const order = [...getOrder(companyId)]
      const fromIdx = order.indexOf(dragKey)
      const toIdx = order.indexOf(key)
      if (fromIdx === -1 || toIdx === -1) return prev
      order.splice(fromIdx, 1)
      order.splice(toIdx, 0, dragKey)
      const next = { ...prev, [companyId]: order }
      savePrefs(next, cardSizes)
      return next
    })
    setDropTarget(null)
  }

  if (view === 'overview') {
    return <Overview onClose={() => setView('dashboard')} />
  }
  if (view === 'import') {
    return <Import onClose={() => setView('dashboard')} />
  }
  if (view === 'receipts') {
    return <Receipts onClose={() => setView('dashboard')} />
  }
  if (view === 'logs') {
    return <Logs onClose={() => setView('dashboard')} />
  }

  return (
    <div className="shell shell--wide">
      <header className="topbar">
        <div className="topbar__brand">Bolag</div>
        <div className="topbar__right">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={{
              background: 'var(--bg-card)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              padding: '0.4rem 0.7rem',
              fontSize: '0.95rem',
            }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {'\u00c5r ' + y}
              </option>
            ))}
          </select>
          <button className="logout-btn" onClick={() => setView('overview')}>
            {'\ud83d\udcca \u00d6versikt'}
          </button>
          <button className="logout-btn" onClick={() => setView('import')}>
            {'\ud83d\udce5 Import'}
          </button>
          <button className="logout-btn" onClick={() => setView('receipts')}>
            {'\ud83e\uddfe Kvitton'}
          </button>
          <button className="logout-btn" onClick={() => setView('logs')}>
            {'\ud83d\udcdc Loggar'}
          </button>
          <ExportButton />
          {user && (
            <>
              <span className="user-chip">
                {user.photoURL ? <img src={user.photoURL} alt="" /> : <span>{'\ud83d\udc64'}</span>}
                <span>{user.email}</span>
              </span>
              <button className="logout-btn" onClick={() => signOut()}>
                Logga ut
              </button>
            </>
          )}
        </div>
      </header>

      {COMPANIES.map((c) => {
        const order = getOrder(c.id)
        const allCards = order.map((key) => ({ key, size: getSize(c.id, key) }))
        const minimized = allCards.filter((cd) => cd.size === 'min')
        const visible = allCards.filter((cd) => cd.size !== 'min')

        return (
          <section key={c.id} className="company-row">
            <h2 className="company-row__name">{c.name}</h2>

            {minimized.length > 0 && (
              <div className="company-row__pills">
                {minimized.map((cd) => (
                  <div key={cd.key}>
                    {renderCard(cd.key, c.id, c.name, 'min', (s) => setSize(c.id, cd.key, s))}
                  </div>
                ))}
              </div>
            )}

            {visible.length > 0 && (
              <div className="company-row__expanded">
                {visible.map((cd) => (
                  <div
                    key={cd.key}
                    className={
                      'company-row__expand-item' +
                      (dropTarget === cd.key && dragCompany === c.id ? ' company-row__expand-item--drop-target' : '')
                    }
                    draggable
                    onDragStart={onDragStart(c.id, cd.key)}
                    onDragEnd={onDragEnd}
                    onDragOver={onDragOver(c.id, cd.key)}
                    onDrop={onDrop(c.id, cd.key)}
                  >
                    {renderCard(cd.key, c.id, c.name, cd.size, (s) => setSize(c.id, cd.key, s))}
                  </div>
                ))}
              </div>
            )}
          </section>
        )
      })}

      <footer style={{ marginTop: '2rem', fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        {COMPANIES.length + ' bolag \u00b7 \u00c5r ' + year + ' \u00b7 Data synkas i realtid via Firestore'}
      </footer>
    </div>
  )
}
